// src/components/pages/dialogs/UnsavedChangesDialog.tsx

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import {
  DLG_BTN_SECONDARY,
  DLG_BTN_DANGER,
} from '@/components/ui/dialogButtons';

interface UnsavedChangesDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title?: string;
  description?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

export default function UnsavedChangesDialog({
  open,
  onConfirm,
  onCancel,
  title = 'Cambios sin guardar',
  description = 'Si sales ahora, perderás los cambios.',
  message = '¿Seguro que quieres salir sin guardar?',
  confirmLabel = 'Salir sin guardar',
  cancelLabel = 'Seguir editando',
}: UnsavedChangesDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:rounded-xl sm:shadow-xl
        "
      >
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
              {title}
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500 dark:text-gray-400">
              {description}
            </DialogDescription>
          </DialogHeader>
          <button
            onClick={onCancel}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 sm:px-6 pb-5">
          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {message}
            </p>
          </div>
        </div>

        <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
          <Button
            variant="outline"
            onClick={onCancel}
            className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
          >
            {cancelLabel}
          </Button>
          <Button
            onClick={onConfirm}
            className={`w-full sm:flex-1 ${DLG_BTN_DANGER}`}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}